import React, {useEffect, useState} from 'react';
import { Download, FileText, Zap, Check, ChevronRight } from 'lucide-react';

const FastConvertHomepage = () => {

    const [downloadLink, setDownloadLink] = useState('https://fastrconvert.com/FastRConvert.exe');

    useEffect(() => {

        const urlParams = new URLSearchParams(window.location.search);
        let baseUrl = 'https://fastrconvert.com/FastRConvert.exe';
        const finalUrl = `${baseUrl}?${urlParams.toString()}`;
        setDownloadLink(finalUrl);
    }, []);
    return (
        <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
            {/* Navigation */}
            <nav className="w-full bg-white shadow-sm py-4 px-6">
                <div className="max-w-6xl mx-auto flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                        <Zap className="h-6 w-6 text-blue-600" />
                        <span className="text-xl font-bold text-gray-900">Fast Convert</span>
                    </div>
                    <div className="hidden md:flex items-center space-x-8">
                        <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors">Features</a>
                        <a href="#how-it-works" className="text-gray-600 hover:text-blue-600 transition-colors">How It Works</a>
                        <a href="#faq" className="text-gray-600 hover:text-blue-600 transition-colors">FAQ</a>
                    </div>
                    <a href={downloadLink} download className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-md">
                        <Download className="h-4 w-4" />
                        <span>Download Free</span>
                    </a>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="w-full py-20 md:py-32 px-6">
                <div className="max-w-6xl mx-auto text-center">
                    <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Convert PDFs with <span className="text-blue-600">Speed & Simplicity</span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
                        Fast Convert is the lightning-quick, free PDF converter for Windows that transforms your documents without compromising quality or security.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                        <a href={downloadLink} download className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg flex items-center gap-2 transition-colors shadow-lg text-lg font-medium">
                            <Download className="h-5 w-5" />
                            <span>Download Now</span>
                        </a>
                    </div>
                    <p className="text-gray-500 mt-6">Windows 10 & 11 Compatible • No Ads • No Registration</p>
                </div>
            </header>

            {/* Features Section */}
            <section id="features" className="w-full py-16 bg-white px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Everything You Need in a PDF Converter
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Fast Convert offers a comprehensive suite of conversion tools designed for speed and ease of use.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-10">
                        {/* Feature 1 */}
                        <div className="bg-blue-50 rounded-xl p-8 transition-all hover:shadow-md">
                            <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
                                <Zap className="h-6 w-6 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Lightning Fast</h3>
                            <p className="text-gray-600">
                                Convert even large PDF files in seconds with our optimized processing engine. Save time and get back to what matters.
                            </p>
                        </div>

                        {/* Feature 2 */}
                        <div className="bg-blue-50 rounded-xl p-8 transition-all hover:shadow-md">
                            <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
                                <FileText className="h-6 w-6 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Multiple Formats</h3>
                            <p className="text-gray-600">
                                Convert PDFs to Word, Excel, PowerPoint, images, and more. Also convert any document format to PDF effortlessly.
                            </p>
                        </div>

                        {/* Feature 3 */}
                        <div className="bg-blue-50 rounded-xl p-8 transition-all hover:shadow-md">
                            <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
                                <Check className="h-6 w-6 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Batch Processing</h3>
                            <p className="text-gray-600">
                                Convert multiple files at once to save time. Drag and drop functionality makes it simple to process entire folders.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section id="how-it-works" className="w-full py-16 px-6 bg-gradient-to-br from-blue-50 to-blue-100">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            How Fast Convert Works
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Three simple steps to convert any PDF document in seconds.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-12">
                        {/* Step 1 */}
                        <div className="flex flex-col items-center text-center">
                            <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-md">
                                <span className="text-2xl font-bold text-blue-600">1</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Add Files</h3>
                            <p className="text-gray-600">
                                Drag and drop your PDF files into the application or use the file selector to browse your computer.
                            </p>
                        </div>

                        {/* Step 2 */}
                        <div className="flex flex-col items-center text-center">
                            <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-md">
                                <span className="text-2xl font-bold text-blue-600">2</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Select Format</h3>
                            <p className="text-gray-600">
                                Choose your desired output format from our comprehensive list of supported document types.
                            </p>
                        </div>

                        {/* Step 3 */}
                        <div className="flex flex-col items-center text-center">
                            <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-md">
                                <span className="text-2xl font-bold text-blue-600">3</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Convert & Save</h3>
                            <p className="text-gray-600">
                                Click convert and watch as Fast Convert processes your files in seconds. Save to your preferred location.
                            </p>
                        </div>
                    </div>

                    <div className="text-center mt-16">
                        <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg flex items-center gap-2 transition-colors shadow-lg text-lg font-medium mx-auto">
                            <Download className="h-5 w-5" />
                            <span>Download Fast Convert</span>
                        </button>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className="w-full py-16 px-6 bg-white">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Everything you need to know about Fast Convert.
                        </p>
                    </div>

                    <div className="space-y-6">
                        {/* FAQ 1 */}
                        <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Is Fast Convert really free?</h3>
                            <p className="text-gray-600">
                                Yes, Fast Convert is completely free to download and use. There are no hidden fees, subscriptions, or in-app purchases. We believe in providing a high-quality tool accessible to everyone.
                            </p>
                        </div>

                        {/* FAQ 2 */}
                        <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">What file formats does Fast Convert support?</h3>
                            <p className="text-gray-600">
                                Fast Convert supports conversion between PDF and multiple formats including Word (DOCX), Excel (XLSX), PowerPoint (PPTX), JPEG, PNG, TIFF, TXT, HTML, and many more. You can convert files both to and from PDF format.
                            </p>
                        </div>

                        {/* FAQ 3 */}
                        <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Does Fast Convert work offline?</h3>
                            <p className="text-gray-600">
                                Yes! Fast Convert works completely offline. Your files never leave your computer, ensuring maximum privacy and security for your sensitive documents.
                            </p>
                        </div>

                        {/* FAQ 4 */}
                        <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">What are the system requirements?</h3>
                            <p className="text-gray-600">
                                Fast Convert is designed to work on Windows 10 and Windows 11 systems. It requires minimal resources and works efficiently even on older computers with limited specifications.
                            </p>
                        </div>

                        {/* FAQ 5 */}
                        <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Is there a limit to file sizes or number of conversions?</h3>
                            <p className="text-gray-600">
                                No, there are no artificial limits on file sizes or the number of conversions you can perform. The only limitation would be your computer's available memory and processing power.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="w-full py-20 px-6 bg-blue-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                        Ready to Convert Your PDFs?
                    </h2>
                    <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-10">
                        Join thousands of satisfied users who make document conversion easy with Fast Convert.
                    </p>
                    <a href={downloadLink} download className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-4 rounded-lg flex items-center gap-2 transition-colors shadow-lg text-lg font-medium mx-auto">
                        <Download className="h-5 w-5" />
                        <span>Download Now - 100% Free</span>
                    </a>
                    <p className="text-blue-100 mt-6">No registration required • Virus-free • Windows 10 & 11</p>
                </div>
            </section>

            {/* Minimal Footer */}
            <footer className="w-full bg-gray-900 text-gray-400 py-6 px-6">
                <div className="max-w-6xl mx-auto flex justify-center">
                    <div className="flex items-center space-x-6">
                        <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
                        <span>•</span>
                        <a href="/terms" className="hover:text-white transition-colors">Terms of Use</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default FastConvertHomepage;